// Cards and the small building blocks inside them: labelled stats, dots, chips and badges.
import * as React from 'react';

import { LayoutSize } from '../../util';
import { CARD_BG, CARD_SHADOW, CARD_SHADOW_RAISED, HAIRLINE_STRONG, INK, INK_SOFT, SECONDARY } from './tokens';

// The tab's page column: 16 px gutters on phones, max 1440 px wide on the web.
export function pageStyle(size: LayoutSize): React.CSSProperties {
  return size === 'phone'
    ? { display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 16px 24px' }
    : size === 'web'
      ? {
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          maxWidth: 1440,
          margin: '0 auto',
          boxSizing: 'border-box',
          padding: '24px 32px 32px',
        }
      : { display: 'flex', flexDirection: 'column', gap: 16, padding: '16px 24px 24px' };
}

export function Card(props: {
  label: string;
  radius?: number; // 20 for the big cards, 16 for the small ones
  raised?: boolean; // the hero card (power flow / airflow) gets a soft drop shadow too
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section
      aria-label={props.label}
      className={props.className}
      style={{
        background: CARD_BG,
        borderRadius: props.radius ?? 20,
        boxShadow: props.raised ? CARD_SHADOW_RAISED : CARD_SHADOW,
        ...props.style,
      }}
    >
      {props.children}
    </section>
  );
}

// Label over a bold value (and an optional sub line). The label is its own element: a label mixing text
// and a value directly inside a flex column breaks into lines.
export function Stat(props: {
  label: string;
  size: number;
  children: React.ReactNode;
  sub?: React.ReactNode;
  subStyle?: React.CSSProperties;
  right?: boolean;
}): React.ReactElement {
  return (
    <span
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        fontSize: 11,
        color: SECONDARY,
        textAlign: props.right ? 'right' : undefined,
        minWidth: 0,
      }}
    >
      <span>{props.label}</span>
      <b style={{ fontSize: props.size, color: INK, whiteSpace: 'nowrap' }}>{props.children}</b>
      {props.sub !== undefined && <span style={{ whiteSpace: 'nowrap', ...props.subStyle }}>{props.sub}</span>}
    </span>
  );
}

export const dotStyle = (color: string, size = 8): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: '50%',
  background: color,
  flex: 'none',
});

export const Dot = (props: { color: string; size?: number; style?: React.CSSProperties }) => (
  <span aria-hidden="true" style={{ ...dotStyle(props.color, props.size), ...props.style }} />
);

// Short coloured line, the legend mark of a chart series.
export const LineMark = (props: { color: string; width?: number; height?: number }) => (
  <span
    aria-hidden="true"
    style={{
      width: props.width ?? 12,
      height: props.height ?? 3,
      borderRadius: 2,
      background: props.color,
      flex: 'none',
    }}
  />
);

export const DashMark = (props: { color: string; style?: React.CSSProperties }) => (
  <span
    aria-hidden="true"
    style={{ width: 12, borderTop: `2px dashed ${props.color}`, flex: 'none', ...props.style }}
  />
);

export const chipStyle: React.CSSProperties = {
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  background: CARD_BG,
  border: `1px solid ${HAIRLINE_STRONG}`,
  borderRadius: 999,
  padding: '6px 10px',
};

// Rounded status badge ("● Auto · live", "Manual override").
export function Badge(props: { bg: string; color?: string; dot?: string; children: React.ReactNode }) {
  return (
    <span
      className="num"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: props.color ?? INK_SOFT,
        background: props.bg,
        borderRadius: 999,
        padding: '3px 10px',
        whiteSpace: 'nowrap',
      }}
    >
      {props.dot && <Dot color={props.dot} size={7} />}
      {props.children}
    </span>
  );
}
